import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi, twoFaApi, configApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { ShieldCheck } from 'lucide-react'

export default function Setup2FaPopup() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [qrData, setQrData] = useState<string | null>(null)
  const [secret, setSecret] = useState('')
  const [otpauthUri, setOtpauthUri] = useState('')
  const [setupCode, setSetupCode] = useState('')

  const abilitato = !!user?.email

  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['me-2fa-popup', user?.id],
    queryFn: () => authApi.me().then(r => r.data),
    enabled: abilitato,
    staleTime: 60_000,
  })

  // Deroga: elenco di user_id esentati dal popup, indipendentemente dal ruolo
  // (gestito da un superadmin in Impostazioni → Sicurezza).
  const { data: deroga, isLoading: derogaLoading } = useQuery({
    queryKey: ['2fa-deroga-tecnici'],
    queryFn: () => configApi.get2faDerogaTecnici().then(r => r.data),
    enabled: abilitato,
    staleTime: 60_000,
  })

  const setupMut = useMutation({
    mutationFn: () => twoFaApi.setup(),
    onSuccess: (res) => { setQrData(res.data.qr); setSecret(res.data.secret); setOtpauthUri(res.data.uri) },
    onError: () => toast.error('Errore avvio setup 2FA'),
  })

  const verifySetupMut = useMutation({
    mutationFn: () => twoFaApi.verifySetup(setupCode),
    onSuccess: () => {
      toast.success('2FA abilitata!')
      setQrData(null); setSetupCode(''); setSecret(''); setOtpauthUri('')
      qc.invalidateQueries({ queryKey: ['me-2fa-popup'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: () => toast.error('Codice non valido'),
  })

  if (!abilitato || meLoading || me?.totp_enabled) return null
  if (derogaLoading || (user?.id != null && deroga?.user_ids?.includes(user.id))) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(8,11,18,0.6)', backdropFilter: 'blur(2px)' }}>
      <div className="card w-full max-w-md" style={{ padding: 24, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: '#dbeafe', color: '#1E40AF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ShieldCheck size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Proteggi il tuo account</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Attiva l'autenticazione a due fattori (2FA)</div>
          </div>
        </div>

        <p style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
          L'attivazione della 2FA è obbligatoria per continuare a usare l'applicazione.
          Ti serve un'app authenticator (es. Google Authenticator):
        </p>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
            target="_blank" rel="noopener noreferrer">
            <img
              src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
              alt="Disponibile su Google Play"
              style={{ height: 40 }}
            />
          </a>
          <a href="https://apps.apple.com/app/google-authenticator/id388497605"
            target="_blank" rel="noopener noreferrer">
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Scarica su App Store"
              style={{ height: 40 }}
            />
          </a>
        </div>

        {!qrData && (
          <button className="btn-primary" style={{ width: '100%' }} onClick={() => setupMut.mutate()} disabled={setupMut.isPending}>
            {setupMut.isPending ? 'Caricamento...' : 'Genera QR code e abilita 2FA'}
          </button>
        )}

        {qrData && (
          <div>
            <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>
              Scansiona il QR code con l'app installata, poi inserisci il codice a 6 cifre per confermare.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <img src={qrData} alt="QR 2FA" style={{ width: 180, height: 180, border: '1px solid #e2e8f0', borderRadius: 8 }} />
            </div>
            {otpauthUri && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                  Stai aprendo questa pagina dallo stesso telefono? Non puoi inquadrare il QR.
                </div>
                <a href={otpauthUri} className="btn-secondary" style={{ fontSize: 12, padding: '6px 14px', display: 'inline-block' }}>
                  Apri direttamente nell'app authenticator
                </a>
              </div>
            )}
            <details style={{ marginBottom: 16 }}>
              <summary style={{ fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>Codice manuale</summary>
              <code style={{ fontSize: 13, letterSpacing: '0.15em', background: '#f8fafc', padding: '4px 8px', borderRadius: 4, display: 'block', marginTop: 6 }}>{secret}</code>
            </details>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={setupCode}
                onChange={e => setSetupCode(e.target.value.replace(/\D/g, ''))}
                style={{ flex: 1, letterSpacing: '0.3em', textAlign: 'center', fontSize: 18 }}
              />
              <button className="btn-primary" onClick={() => verifySetupMut.mutate()}
                disabled={setupCode.length !== 6 || verifySetupMut.isPending}>
                {verifySetupMut.isPending ? '...' : 'Conferma'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
